import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  Chip
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import { toast } from 'react-toastify';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setSent(true);
      toast.success('Reset link sent to your email!');
    } catch (err) {
      setError('Failed to send reset link. Please try again.');
      toast.error('Failed to send reset link.');
    }

    setLoading(false);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 700,
          textAlign: 'center',
          mb: 1,
          color: 'text.primary'
        }}
      >
        Forgot Password
      </Typography>
      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          mb: 3,
          color: 'text.secondary',
          fontWeight: 500
        }}
      >
        Enter your email to receive a reset link
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {sent && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          A password reset link has been sent to {email}. Please check your inbox.
        </Alert>
      )}

      <TextField
        fullWidth
        label="Email"
        name="email"
        type="email"
        value={email}
        onChange={handleChange}
        margin="normal"
        required
        autoComplete="email"
        disabled={sent}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon sx={{ color: '#6366f1' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: '#6366f1',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#6366f1',
              borderWidth: '2px',
            },
          },
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading || sent}
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
        sx={{
          mt: 3,
          mb: 2,
          py: 1.5,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1rem',
          borderRadius: 3,
          boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.5)',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%)',
            boxShadow: '0 15px 35px -5px rgba(99, 102, 241, 0.6)',
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        }}
      >
        {loading ? 'Sending...' : 'Send Reset Link'}
      </Button>

      <Divider sx={{ my: 2 }}>
        <Chip label="or" size="small" sx={{ color: 'text.secondary', fontWeight: 500 }} />
      </Divider>

      <Typography variant="body2" align="center" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        Remember your password?{' '}
        <Link
          to="/auth/login"
          style={{
            color: '#6366f1',
            textDecoration: 'none',
            fontWeight: 700,
            borderBottom: '2px solid transparent',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => { e.target.style.borderBottom = '2px solid #6366f1'; }}
          onMouseLeave={(e) => { e.target.style.borderBottom = '2px solid transparent'; }}
        >
          Sign In
        </Link>
      </Typography>
    </Box>
  );
};

export default ForgotPassword;
